/**
 * Rebel Tide Hoodies — theme layer on top of Horizon.
 * Small, dependency-free custom elements. Loaded once as a module.
 *
 *  - RT.money: converts store-currency amounts into the visitor's currency and formats them
 *    exactly like Shopify's `money` filter does on this store.
 *  - <rt-money>: renders a converted amount (free shipping threshold, pack discounts...).
 *  - <rt-intro>: the "001 OF 300" opening (numbers roll, then the photo opens).
 *  - <rt-stock>: real stock of the selected size on the product page.
 *  - <rt-count>: counts up to a number when it scrolls into view.
 *  - <rt-tabs>: accessible tabs.
 *  - <rt-picker>: model + size picker used by the pack builder and the cart.
 *  - <rt-pack-builder>: "Build your pack" module (home, collection, product page).
 *  - <rt-pack-offers>: "Complete your pack" invitations inside the cart.
 *  - <rt-ship-bar>: free shipping progress.
 *  - reveal on scroll for [data-rt-reveal].
 */

const RT = (window.RT = window.RT || {});
const cfg = RT.config || {};
const html = document.documentElement;

/* ------------------------------------------------------------------ *
 * Motion helpers
 * ------------------------------------------------------------------ */

const motionLevel = () => html.getAttribute('data-rt-motion') || 'full';
const motionOn = () => motionLevel() !== 'off';

/* ------------------------------------------------------------------ *
 * Money
 * ------------------------------------------------------------------ */

const money = (() => {
  const sample = String(cfg.moneySample || '$1,234,567.89');
  const numberMatch = sample.match(/\d[\d.,'\s  ]*\d/);
  const numberPart = numberMatch ? numberMatch[0] : '1,234,567.89';
  const template = numberMatch ? sample.replace(numberPart, '{{amount}}') : '{{amount}}';
  const decimalMatch = numberPart.match(/([.,])(\d{2})$/);
  const hasDecimals = Boolean(decimalMatch);
  const decimalSep = decimalMatch ? decimalMatch[1] : '.';
  const intPart = decimalMatch ? numberPart.slice(0, -3) : numberPart;
  const thousandsMatch = intPart.match(/\d([.,'\s  ])\d{3}/);
  const thousandsSep = thousandsMatch ? thousandsMatch[1] : '';

  /**
   * @param {number} cents
   * @param {{ trim?: boolean }} [options] - trim: drop ".00" on whole amounts
   */
  function format(cents, options = {}) {
    const value = Math.round(Number(cents) || 0) / 100;
    const fixed = hasDecimals ? value.toFixed(2) : String(Math.round(value));
    const [intStr, decStr] = fixed.split('.');
    const withThousands = intStr.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
    const keepDecimals = hasDecimals && !(options.trim && decStr === '00');
    const amount = keepDecimals ? `${withThousands}${decimalSep}${decStr}` : withThousands;
    return template.replace('{{amount}}', amount);
  }

  function activeCurrency() {
    return window.Shopify?.currency?.active || cfg.cartCurrency || cfg.shopCurrency;
  }

  function rate() {
    const r = parseFloat(window.Shopify?.currency?.rate || '1');
    return Number.isFinite(r) && r > 0 ? r : 1;
  }

  function isShopCurrency() {
    return activeCurrency() === cfg.shopCurrency;
  }

  function overrides() {
    const out = {};
    String(cfg.freeShipping?.overrides || '')
      .split(/[,;\n]/)
      .forEach((pair) => {
        const [code, value] = pair.split(':').map((s) => s && s.trim());
        const n = parseFloat(String(value || '').replace(',', '.'));
        if (code && Number.isFinite(n)) out[code.toUpperCase()] = Math.round(n * 100);
      });
    return out;
  }

  /**
   * Converts an amount in store currency cents to the visitor's currency cents.
   * @param {number} baseCents
   * @param {{ kind?: 'amount' | 'threshold' }} [options]
   */
  function fromBase(baseCents, options = {}) {
    const base = Math.round(Number(baseCents) || 0);
    if (options.kind === 'threshold') {
      const fixed = overrides()[String(activeCurrency()).toUpperCase()];
      if (fixed !== undefined) return fixed;
    }
    if (isShopCurrency()) return base;
    const converted = base * rate();
    // Thresholds are shown rounded up to a whole unit so the promise is never lower than the rule.
    return options.kind === 'threshold' ? Math.ceil(converted / 100) * 100 : Math.round(converted);
  }

  return { format, fromBase, isShopCurrency, activeCurrency, rate };
})();

RT.money = money;

class RtMoney extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  render() {
    const base = Number(this.dataset.base);
    if (!Number.isFinite(base)) return;
    if (this.hasAttribute('data-ready') && money.isShopCurrency() && !this.dataset.force) return;
    const kind = this.dataset.kind === 'threshold' ? 'threshold' : 'amount';
    this.textContent = money.format(money.fromBase(base, { kind }), { trim: this.hasAttribute('data-trim') });
    this.setAttribute('data-ready', '');
  }
}

/* ------------------------------------------------------------------ *
 * Reveal on scroll
 * ------------------------------------------------------------------ */

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-in');
              revealObserver?.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
      )
    : null;

function scanReveals(root = document) {
  const nodes = root.querySelectorAll('[data-rt-reveal]:not(.is-in)');
  nodes.forEach((node) => {
    if (!revealObserver || !motionOn()) node.classList.add('is-in');
    else revealObserver.observe(node);
  });
}

/* ------------------------------------------------------------------ *
 * <rt-intro> lives in rt-intro.js: its section loads it right after the
 * element, so the 001 → 300 count starts as soon as the page shows.
 * Theme editor: a section added live doesn't run that script tag, so it
 * is loaded from here.
 * ------------------------------------------------------------------ */

document.addEventListener('shopify:section:load', (event) => {
  if (customElements.get('rt-intro')) return;
  const tag = event.target instanceof Element ? event.target.querySelector('script[data-rt-intro-js]') : null;
  if (!(tag instanceof HTMLScriptElement) || !tag.src) return;
  const script = document.createElement('script');
  script.src = tag.src;
  document.head.appendChild(script);
});

/* ------------------------------------------------------------------ *
 * <rt-count>
 * ------------------------------------------------------------------ */

class RtCount extends HTMLElement {
  connectedCallback() {
    const to = Number(this.dataset.to || this.textContent) || 0;
    const pad = Number(this.dataset.pad || 0);
    this.to = to;
    this.pad = pad;
    if (!motionOn() || !('IntersectionObserver' in window)) {
      this.set(to);
      return;
    }
    this.io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.io?.disconnect();
          this.run();
        }
      },
      { threshold: 0.4 }
    );
    // The real number stays until the counter gets close to the screen, so page captures and
    // crawlers never see "000"; it drops to 0 just before it comes into view, then counts up.
    if (this.getBoundingClientRect().top < window.innerHeight * 1.5) {
      this.set(0);
      this.io.observe(this);
      return;
    }
    this.set(to);
    this.near = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.near?.disconnect();
        this.set(0);
        this.io?.observe(this);
      },
      { rootMargin: '0px 0px 50% 0px' }
    );
    this.near.observe(this);
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.near?.disconnect();
  }

  /** @param {number} value */
  set(value) {
    const s = String(Math.round(value));
    this.textContent = this.pad ? s.padStart(this.pad, '0') : s;
  }

  run() {
    const duration = Number(this.dataset.duration || 1400);
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 4);
      this.set(this.to * eased);
      if (t < 1) requestAnimationFrame(tick);
      else this.classList.add('is-done');
    };
    requestAnimationFrame(tick);
  }
}

/* ------------------------------------------------------------------ *
 * <rt-tabs>
 * ------------------------------------------------------------------ */

class RtTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index, true));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const dir = event.key === 'ArrowRight' ? 1 : -1;
        const next = (index + dir + this.tabs.length) % this.tabs.length;
        this.select(next, true);
      });
    });
  }

  /** @param {number} index @param {boolean} focus */
  select(index, focus) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
      if (panel) {
        panel.hidden = !active;
        if (active) scanReveals(panel);
      }
    });
    if (focus) this.tabs[index]?.focus();
  }
}

/* ------------------------------------------------------------------ *
 * Pack data (eligible hoodies and tees) — loaded once, lazily
 * ------------------------------------------------------------------ */

let packDataPromise = null;

RT.loadPackData = function loadPackData() {
  if (packDataPromise) return packDataPromise;
  const inline = document.getElementById('rt-pack-data');
  if (inline) {
    packDataPromise = Promise.resolve(JSON.parse(inline.textContent || '{}'));
    return packDataPromise;
  }
  const url = `${cfg.routes?.root || '/'}?section_id=rt-pack-picker`;
  packDataPromise = fetch(url, { credentials: 'same-origin' })
    .then((r) => {
      if (!r.ok) throw new Error(`Pack data ${r.status}`);
      return r.text();
    })
    .then((text) => {
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const script = doc.querySelector('#rt-pack-data');
      return JSON.parse(script?.textContent || '{}');
    })
    .catch((error) => {
      packDataPromise = null;
      throw error;
    });
  return packDataPromise;
};

/* ------------------------------------------------------------------ *
 * Cart add (keeps Horizon's cart drawer, counts and events in sync)
 * ------------------------------------------------------------------ */

/**
 * @param {{id: number|string, quantity?: number}[]} items
 * @param {HTMLElement} source
 * @param {'cart'|'product'} [context]
 */
RT.addToCart = async function addToCart(items, source, context = 'product') {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);

  let Events = null;
  try {
    Events = await import('@shopify/events');
  } catch (e) {
    Events = null;
  }

  const CartLinesUpdateEvent = Events?.CartLinesUpdateEvent;
  const deferred = CartLinesUpdateEvent ? CartLinesUpdateEvent.createPromise() : null;
  const lines = items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity || 1 }));

  if (CartLinesUpdateEvent && deferred) {
    source.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context, lines, promise: deferred.promise }));
  }

  const response = await fetch(`${(cfg.routes?.cartAdd || '/cart/add').replace(/\.js$/, '')}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items: items.map((item) => ({ id: Number(item.id), quantity: item.quantity || 1 })),
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  });
  const data = await response.json().catch(() => ({}));
  const cart = await fetch(`${cfg.routes?.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
    r.json()
  );

  const failed = !response.ok || data.status;
  if (deferred && CartLinesUpdateEvent) {
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        source: 'rt-pack',
        sections: failed ? undefined : data.sections,
        didError: Boolean(failed),
      },
    });
  }

  if (failed) {
    throw new Error(data.description || data.message || cfg.strings?.addFailed || 'Add to cart failed');
  }

  if (!sectionIds.length) {
    window.location.href = cfg.routes?.cart || '/cart';
  }
  return cart;
};

/* ------------------------------------------------------------------ *
 * <rt-picker> — model + size
 * ------------------------------------------------------------------ */

const ICON_CHECK =
  '<svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 8.5l3 3 7-7"/></svg>';

class RtPicker extends HTMLElement {
  /** @type {any[]} */
  models = [];
  /** @type {any} */
  model = null;
  /** @type {any} */
  variant = null;

  /**
   * @param {any[]} models
   * @param {{ preferSize?: string, preferVariant?: string|number, avoid?: string[], label?: string, preferModel?: string }} options
   */
  setup(models, options = {}) {
    this.models = (models || []).filter((m) => m.variants?.length && m.available);
    this.options = options;
    const uid = Math.random().toString(36).slice(2, 8);
    const label = options.label || cfg.strings?.choose || 'Choose';
    const sizeLabel = cfg.strings?.size || 'Size';
    this.innerHTML = `
      <div class="rt-picker__label"><span>${escapeHtml(label)}</span><strong data-model-name></strong></div>
      <div class="rt-picker__models" role="radiogroup" aria-label="${escapeHtml(label)}"></div>
      <div class="rt-picker__label"><span>${escapeHtml(sizeLabel)}</span><strong data-size-name></strong></div>
      <div class="rt-picker__sizes" role="radiogroup" aria-label="${escapeHtml(sizeLabel)}" id="rt-sizes-${uid}"></div>
    `;
    const list = this.querySelector('.rt-picker__models');
    this.models.forEach((model, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'rt-picker__model';
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', 'false');
      button.setAttribute('aria-label', model.title);
      button.dataset.index = String(index);
      if (!model.available) button.dataset.soldout = '';
      button.innerHTML = model.image
        ? `<img src="${model.image}" alt="" width="64" height="80" loading="lazy">`
        : `<span class="rt-visually-hidden">${escapeHtml(model.title)}</span>`;
      button.addEventListener('click', () => this.selectModel(index, true));
      list?.appendChild(button);
    });

    const avoid = options.avoid || [];
    const preferId = String(options.preferVariant || '');
    let start = preferId ? this.models.findIndex((m) => m.variants.some((v) => v.available && String(v.id) === preferId)) : -1;
    if (start < 0) start = this.models.findIndex((m) => m.available && options.preferModel && m.key === options.preferModel);
    if (start < 0) start = this.models.findIndex((m) => m.available && !avoid.includes(m.handle));
    if (start < 0) start = this.models.findIndex((m) => m.available);
    this.selectModel(Math.max(start, 0), false);
  }

  /** @param {number} index @param {boolean} user */
  selectModel(index, user) {
    this.model = this.models[index];
    if (!this.model) return;
    this.querySelectorAll('.rt-picker__model').forEach((b, i) => b.setAttribute('aria-checked', String(i === index)));
    const nameEl = this.querySelector('[data-model-name]');
    if (nameEl) nameEl.textContent = this.model.title;
    if (user) {
      const button = this.querySelectorAll('.rt-picker__model')[index];
      button?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: motionOn() ? 'smooth' : 'auto' });
    }
    this.renderSizes();
  }

  renderSizes() {
    const wrap = this.querySelector('.rt-picker__sizes');
    if (!wrap || !this.model) return;
    wrap.innerHTML = '';
    const prefer = String(this.variant?.size || this.options?.preferSize || '').toUpperCase();
    const preferId = String(this.options?.preferVariant || '');
    let byId = -1;
    let bySize = -1;
    this.model.variants.forEach((variant, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'rt-size';
      button.textContent = variant.size;
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', 'false');
      button.disabled = !variant.available;
      if (!variant.available) button.setAttribute('aria-label', `${variant.size}, ${cfg.strings?.soldOut || 'sold out'}`);
      button.addEventListener('click', () => this.selectVariant(index));
      wrap.appendChild(button);
      if (!variant.available) return;
      if (byId < 0 && preferId && String(variant.id) === preferId) byId = index;
      if (bySize < 0 && String(variant.size).toUpperCase() === prefer) bySize = index;
    });
    const selectedIndex = byId >= 0 ? byId : bySize;
    this.variant = null;
    if (selectedIndex >= 0) this.selectVariant(selectedIndex);
    else this.emit();
    const sizeName = this.querySelector('[data-size-name]');
    if (sizeName && !this.variant) sizeName.textContent = cfg.strings?.chooseSize || 'Choose a size';
  }

  /** @param {number} index */
  selectVariant(index) {
    const variant = this.model?.variants[index];
    if (!variant || !variant.available) return;
    this.variant = variant;
    this.querySelectorAll('.rt-size').forEach((b, i) => b.setAttribute('aria-checked', String(i === index)));
    const sizeName = this.querySelector('[data-size-name]');
    if (sizeName) sizeName.textContent = variant.size;
    this.emit();
  }

  /** Selects the size (and model) of a variant id, if it is offered here. @param {string|number} id */
  selectVariantById(id) {
    const target = String(id || '');
    const modelIndex = this.models.findIndex((m) => m.variants.some((v) => v.available && String(v.id) === target));
    if (modelIndex < 0) return false;
    if (this.models[modelIndex] !== this.model) {
      this.options = { ...this.options, preferVariant: target };
      this.selectModel(modelIndex, false);
    } else {
      const index = this.model.variants.findIndex((v) => String(v.id) === target);
      if (index >= 0) this.selectVariant(index);
    }
    return true;
  }

  emit() {
    this.dispatchEvent(
      new CustomEvent('rt:picker-change', {
        bubbles: true,
        detail: { model: this.model, variant: this.variant },
      })
    );
  }
}

/** @param {string} s */
function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

/* ------------------------------------------------------------------ *
 * Pack math (shared)
 * ------------------------------------------------------------------ */

/** @param {'rebel'|'surf'} pack */
function packDiscount(pack) {
  const base = cfg.packs?.[pack]?.discountBase || 0;
  return money.fromBase(base, { kind: 'amount' });
}

function freeShippingThreshold() {
  return money.fromBase(cfg.freeShipping?.base || 0, { kind: 'threshold' });
}

/**
 * Pack card numbers in the visitor's currency: total = the two prices less the pack discount;
 * crossed-out = the two compare-at prices (the price when a piece has none); saving and % from both.
 * Reads data-price-a/b and data-compare-a/b, fills [data-rt-calc="total|was|save|pct"].
 * @param {HTMLElement} el
 * @param {'rebel'|'surf'} pack
 */
function setPackCalcs(el, pack) {
  const priceA = Number(el.dataset.priceA) || 0;
  const priceB = Number(el.dataset.priceB) || 0;
  const was = Math.max(priceA, Number(el.dataset.compareA) || 0) + Math.max(priceB, Number(el.dataset.compareB) || 0);
  const total = Math.max(0, priceA + priceB - packDiscount(pack));
  const save = Math.max(0, was - total);
  const pct = was > 0 ? Math.round((save * 100) / was) : 0;
  el.querySelectorAll('[data-rt-calc]').forEach((node) => {
    const target = /** @type {HTMLElement} */ (node);
    switch (target.dataset.rtCalc) {
      case 'total':
        target.textContent = money.format(total);
        break;
      case 'was':
        target.textContent = money.format(was);
        break;
      case 'save':
        target.textContent = money.format(save, { trim: true });
        break;
      case 'pct':
        target.textContent = (target.dataset.template || '[n]%').replace('[n]', String(pct));
        break;
    }
  });
}

/* ------------------------------------------------------------------ *
 * <rt-pack-builder> — home / collection, and the product page
 * On the product page (data-this-handle) the first piece is the product
 * itself, in the size picked on the page; the pack cards open and close
 * the builder (data-toggle).
 * ------------------------------------------------------------------ */

class RtPackBuilder extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[data-pack-tab]')];
    this.tabs.forEach((tab) =>
      tab.addEventListener('click', () => {
        const wasCollapsed = this.hasAttribute('data-collapsed');
        if (!wasCollapsed && this.hasAttribute('data-toggle') && tab.dataset.packTab === this.pack) {
          this.collapse();
          return;
        }
        this.removeAttribute('data-collapsed');
        this.start?.();
        this.setPack(tab.dataset.packTab, true);
        if (wasCollapsed) this.querySelector('[data-builder-body]')?.scrollIntoView({ block: 'nearest', behavior: motionOn() ? 'smooth' : 'auto' });
      })
    );
    this.addButton = this.querySelector('[data-pack-add]');
    this.addButton?.addEventListener('click', () => this.add());
    this.addEventListener('rt:picker-change', (event) => {
      // Product page: remember the size picked for this product in the builder too.
      const variant = /** @type {CustomEvent} */ (event).detail?.variant;
      const slot = event.target instanceof HTMLElement ? event.target.dataset.slot : '';
      if (this.dataset.thisHandle && slot === 'a' && variant) this.dataset.thisVariant = String(variant.id);
      this.update();
    });
    this.pack = this.dataset.defaultPack || 'rebel';
    this.renderTabs();

    // Product page: the first piece follows the size picked in the product form.
    if (this.dataset.thisHandle) {
      this.scope = this.closest('.shopify-section, dialog');
      this.onProductSelect = (event) => {
        const target = event.target;
        if (!(target instanceof Element) || target.closest('product-card') || this.contains(target)) return;
        const id = /** @type {any} */ (event).detail?.variantId;
        if (!id) return;
        this.dataset.thisVariant = String(id);
        const slotA = this.querySelector('[data-slot="a"]');
        if (slotA instanceof RtPicker && slotA.model) slotA.selectVariantById(id);
      };
      this.scope?.addEventListener('shopify:product:select', this.onProductSelect);
    }

    const start = (this.start = () => {
      if (this.started) return;
      this.started = true;
      RT.loadPackData()
        .then((data) => {
          this.data = data;
          this.setPack(this.pack, false);
        })
        .catch(() => this.showError(cfg.strings?.addFailed));
    });

    if (this.hasAttribute('data-collapsed')) return;

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.io?.disconnect();
            start();
          }
        },
        { rootMargin: '400px 0px' }
      );
      this.io.observe(this);
    } else start();
  }

  disconnectedCallback() {
    this.io?.disconnect();
    if (this.onProductSelect) this.scope?.removeEventListener('shopify:product:select', this.onProductSelect);
  }

  collapse() {
    this.setAttribute('data-collapsed', '');
    this.syncTabs();
  }

  syncTabs() {
    const toggle = this.hasAttribute('data-toggle');
    const open = !this.hasAttribute('data-collapsed');
    this.tabs.forEach((tab) => {
      const on = tab.dataset.packTab === this.pack && (!toggle || open);
      tab.setAttribute('aria-selected', String(on));
      if (toggle) tab.setAttribute('aria-expanded', String(on));
    });
  }

  /** Which pieces each slot offers. */
  slotModels() {
    const hoodies = this.data?.hoodies || [];
    const tees = this.data?.tees || [];
    const own = this.dataset.thisHandle;
    if (!own) return { modelsA: hoodies, modelsB: this.pack === 'rebel' ? hoodies : tees, avoidB: null };
    const isTee = this.dataset.thisKind === 'tee';
    const ownList = isTee ? tees : hoodies;
    let mine = ownList.filter((m) => m.handle === own && m.available);
    // This product is not in the pack list (sold out, left out): any piece of its kind.
    if (!mine.length) mine = ownList;
    if (this.pack === 'rebel') return { modelsA: mine, modelsB: hoodies, avoidB: [own] };
    return { modelsA: mine, modelsB: isTee ? hoodies : tees, avoidB: [] };
  }

  /** @param {string|undefined} pack @param {boolean} user */
  setPack(pack, user) {
    this.pack = pack === 'surf' ? 'surf' : 'rebel';
    this.syncTabs();
    this.querySelectorAll('[data-pack-copy]').forEach((el) => {
      el.hidden = el.dataset.packCopy !== this.pack;
    });
    if (!this.data) return;
    const slotA = this.querySelector('[data-slot="a"]');
    const slotB = this.querySelector('[data-slot="b"]');
    const labelA = this.dataset[`${this.pack}LabelA`] || 'Hoodie';
    const labelB = this.dataset[`${this.pack}LabelB`] || (this.pack === 'rebel' ? 'Second hoodie' : 'Oversized tee');
    const { modelsA, modelsB, avoidB } = this.slotModels();
    if (slotA instanceof RtPicker) slotA.setup(modelsA, { label: labelA, preferSize: 'M', preferVariant: this.dataset.thisVariant });
    if (slotB instanceof RtPicker) {
      const avoid = avoidB || (this.pack === 'rebel' && slotA instanceof RtPicker && slotA.model ? [slotA.model.handle] : []);
      const sizeA = this.dataset.thisHandle && slotA instanceof RtPicker ? slotA.variant?.size : '';
      slotB.setup(modelsB, { label: labelB, preferSize: sizeA || 'M', avoid });
    }
    this.querySelector('[data-builder-body]')?.removeAttribute('data-loading');
    this.update();
    if (user) this.querySelector('[data-builder-body]')?.scrollIntoView({ block: 'nearest', behavior: motionOn() ? 'smooth' : 'auto' });
  }

  /** Pack cards: totals in the visitor's currency (server renders store currency). */
  renderTabs() {
    this.tabs.forEach((tab) => {
      const pack = tab.dataset.packTab === 'surf' ? 'surf' : 'rebel';
      if (!money.isShopCurrency()) setPackCalcs(tab, pack);
    });
    if (!money.isShopCurrency()) this.update();
    this.classList.remove('rt-pending-currency');
  }

  update() {
    const a = this.querySelector('[data-slot="a"]');
    const b = this.querySelector('[data-slot="b"]');
    const va = a instanceof RtPicker ? a.variant : null;
    const vb = b instanceof RtPicker ? b.variant : null;
    const priceA = va?.price ?? a?.model?.price ?? 0;
    const priceB = vb?.price ?? b?.model?.price ?? 0;
    // Crossed-out price = compare-at prices (the regular price when a piece has none).
    const wasA = Math.max(priceA, va?.compare ?? a?.model?.compare ?? 0);
    const wasB = Math.max(priceB, vb?.compare ?? b?.model?.compare ?? 0);
    const was = wasA + wasB;
    const total = Math.max(0, priceA + priceB - packDiscount(this.pack));
    const save = Math.max(0, was - total);
    this.setText('[data-builder-total]', money.format(total));
    this.setText('[data-builder-was]', money.format(was));
    this.setText('[data-builder-save]', money.format(save, { trim: true }));
    const ship = this.querySelector('[data-builder-ship]');
    if (ship) ship.hidden = !(total >= freeShippingThreshold());
    if (this.addButton) {
      const ready = Boolean(va && vb);
      this.addButton.toggleAttribute('disabled', !ready);
      const label = this.addButton.querySelector('[data-label]');
      if (label) {
        const name = cfg.packs?.[this.pack]?.name || 'pack';
        const template = cfg.strings?.addPack || 'Add [pack] — [price]';
        label.textContent = ready
          ? template.replace('[pack]', name).replace('[price]', money.format(total))
          : cfg.strings?.chooseSize || 'Choose a size';
      }
    }
  }

  /** @param {string} selector @param {string} text */
  setText(selector, text) {
    this.querySelectorAll(selector).forEach((el) => (el.textContent = text));
  }

  /** @param {string|undefined} message */
  showError(message) {
    const el = this.querySelector('[data-builder-error]');
    if (el) {
      el.textContent = message || '';
      el.hidden = !message;
    }
  }

  async add() {
    const a = this.querySelector('[data-slot="a"]');
    const b = this.querySelector('[data-slot="b"]');
    const va = a instanceof RtPicker ? a.variant : null;
    const vb = b instanceof RtPicker ? b.variant : null;
    if (!va || !vb || !this.addButton) return;
    this.showError('');
    this.addButton.classList.add('is-loading');
    try {
      const items = va.id === vb.id ? [{ id: va.id, quantity: 2 }] : [{ id: va.id, quantity: 1 }, { id: vb.id, quantity: 1 }];
      await RT.addToCart(items, this, 'product');
    } catch (error) {
      this.showError(error?.message);
    } finally {
      this.addButton.classList.remove('is-loading');
    }
  }
}

/* ------------------------------------------------------------------ *
 * <rt-pack-offers> — inside the cart drawer / cart page
 * ------------------------------------------------------------------ */

class RtPackOffers extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('rt:picker-change', this.onPickerChange);
    this.refresh();
    this.mo = new MutationObserver(() => {
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => this.refresh());
    });
    this.mo.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-cart-total'] });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('rt:picker-change', this.onPickerChange);
    this.mo?.disconnect();
  }

  /** Recomputes converted amounts for non store-currency visitors. */
  refresh() {
    if (money.isShopCurrency()) return;
    this.querySelectorAll('[data-offer]').forEach((offer) => this.computeOffer(/** @type {HTMLElement} */ (offer)));
    this.querySelectorAll('rt-money').forEach((el) => /** @type {RtMoney} */ (el).render?.());
  }

  /**
   * @param {HTMLElement} offer
   * @param {number} [addPriceOverride]
   */
  computeOffer(offer, addPriceOverride) {
    const pack = offer.dataset.offer === 'surf' ? 'surf' : 'rebel';
    const cartTotal = Number(this.dataset.cartTotal) || 0;
    const addPrice = addPriceOverride ?? (Number(offer.dataset.addPrice) || 0);
    const save = packDiscount(pack);
    const extra = Math.max(0, addPrice - save);
    const total = cartTotal + extra;
    const set = (sel, text) => offer.querySelectorAll(sel).forEach((el) => (el.textContent = text));
    set('[data-offer-extra]', money.format(extra));
    set('[data-offer-total]', money.format(total));
    set('[data-offer-save]', money.format(save));
    const ship = offer.querySelector('[data-offer-ship]');
    if (ship) ship.hidden = !(total >= freeShippingThreshold() && cartTotal < freeShippingThreshold());
    const confirm = offer.querySelector('[data-offer-confirm] [data-label]');
    if (confirm) {
      const picker = offer.querySelector('rt-picker');
      const ready = picker instanceof RtPicker && Boolean(picker.variant);
      confirm.textContent = ready ? `${offer.dataset.cta || 'Add'} — ${money.format(total)}` : cfg.strings?.chooseSize || 'Choose a size';
    }
  }

  /** @param {Event} event */
  onPickerChange = (event) => {
    const offer = /** @type {HTMLElement|null} */ (event.target instanceof Element ? event.target.closest('[data-offer]') : null);
    if (!offer) return;
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    const price = detail.variant?.price ?? detail.model?.price;
    this.computeOffer(offer, Number.isFinite(price) ? price : undefined);
    const confirm = offer.querySelector('[data-offer-confirm]');
    if (confirm) confirm.toggleAttribute('disabled', !detail.variant);
  };

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const open = target?.closest('[data-offer-open]');
    if (open) {
      event.preventDefault();
      const offer = /** @type {HTMLElement|null} */ (open.closest('[data-offer]'));
      if (offer) this.openOffer(offer);
      return;
    }
    const close = target?.closest('[data-offer-close]');
    if (close) {
      event.preventDefault();
      const offer = /** @type {HTMLElement|null} */ (close.closest('[data-offer]'));
      if (offer) this.closeOffer(offer);
      return;
    }
    const confirm = target?.closest('[data-offer-confirm]');
    if (confirm) {
      event.preventDefault();
      const offer = /** @type {HTMLElement|null} */ (confirm.closest('[data-offer]'));
      if (offer) this.confirmOffer(offer, /** @type {HTMLElement} */ (confirm));
    }
  };

  /** @param {HTMLElement} offer */
  openOffer(offer) {
    this.querySelectorAll('[data-offer].is-open').forEach((other) => {
      if (other !== offer) this.closeOffer(/** @type {HTMLElement} */ (other));
    });
    offer.classList.add('is-open');
    offer.querySelector('[data-offer-open]')?.setAttribute('aria-expanded', 'true');
    const panel = /** @type {HTMLElement|null} */ (offer.querySelector('[data-offer-panel]'));
    if (panel) panel.hidden = false;
    const picker = offer.querySelector('rt-picker');
    if (!(picker instanceof RtPicker) || picker.dataset.ready) {
      panel?.scrollIntoView({ block: 'nearest', behavior: motionOn() ? 'smooth' : 'auto' });
      return;
    }
    RT.loadPackData()
      .then((data) => {
        const kind = offer.dataset.addKind === 'tee' ? 'tees' : 'hoodies';
        const exclude = (offer.dataset.avoid || '').split(',').filter(Boolean);
        picker.setup(data[kind] || [], {
          label: offer.dataset.pickLabel || 'Choose',
          preferSize: offer.dataset.size || 'M',
          avoid: exclude,
        });
        picker.dataset.ready = 'true';
        offer.querySelector('[data-offer-loading]')?.remove();
        panel?.scrollIntoView({ block: 'nearest', behavior: motionOn() ? 'smooth' : 'auto' });
      })
      .catch(() => {
        const err = offer.querySelector('[data-offer-error]');
        if (err) {
          err.textContent = cfg.strings?.addFailed || '';
          err.hidden = false;
        }
      });
  }

  /** @param {HTMLElement} offer */
  closeOffer(offer) {
    offer.classList.remove('is-open');
    offer.querySelector('[data-offer-open]')?.setAttribute('aria-expanded', 'false');
    const panel = /** @type {HTMLElement|null} */ (offer.querySelector('[data-offer-panel]'));
    if (panel) panel.hidden = true;
  }

  /** @param {HTMLElement} offer @param {HTMLElement} button */
  async confirmOffer(offer, button) {
    const picker = offer.querySelector('rt-picker');
    const variant = picker instanceof RtPicker ? picker.variant : null;
    if (!variant) return;
    const err = offer.querySelector('[data-offer-error]');
    if (err) err.hidden = true;
    button.classList.add('is-loading');
    try {
      await RT.addToCart([{ id: variant.id, quantity: 1 }], this, 'cart');
    } catch (error) {
      if (err) {
        err.textContent = error?.message || cfg.strings?.addFailed || '';
        err.hidden = false;
      }
    } finally {
      button.classList.remove('is-loading');
    }
  }
}

/* ------------------------------------------------------------------ *
 * <rt-ship-bar>
 * ------------------------------------------------------------------ */

class RtShipBar extends HTMLElement {
  connectedCallback() {
    this.render();
    this.mo = new MutationObserver(() => this.render());
    this.mo.observe(this, { attributes: true, attributeFilter: ['data-total'] });
  }

  disconnectedCallback() {
    this.mo?.disconnect();
  }

  render() {
    const total = Number(this.dataset.total) || 0;
    const threshold = freeShippingThreshold();
    if (!threshold) return;
    const progress = Math.max(0, Math.min(1, total / threshold));
    this.style.setProperty('--rt-progress', String(progress));
    const text = this.querySelector('[data-ship-text]');
    const unlocked = total >= threshold;
    this.toggleAttribute('data-unlocked', unlocked);
    if (text && (!money.isShopCurrency() || !text.hasAttribute('data-server'))) {
      text.textContent = unlocked
        ? cfg.strings?.freeShippingUnlocked || 'Free shipping unlocked'
        : (cfg.strings?.awayFromFreeShipping || "You're [amount] away from free shipping").replace(
            '[amount]',
            money.format(threshold - total)
          );
    }
    text?.removeAttribute('data-server');
  }
}

/* ------------------------------------------------------------------ *
 * Static pack price boxes (product page): convert for other currencies
 * ------------------------------------------------------------------ */

function renderPackCalcs(root = document) {
  root.querySelectorAll('[data-rt-pack-calcs]').forEach((box) => {
    if (!money.isShopCurrency()) {
      box.querySelectorAll('[data-pack]').forEach((row) => {
        const pack = /** @type {HTMLElement} */ (row).dataset.pack === 'surf' ? 'surf' : 'rebel';
        setPackCalcs(/** @type {HTMLElement} */ (row), pack);
      });
    }
    box.classList.remove('rt-pending-currency');
  });
}

/* ------------------------------------------------------------------ *
 * <rt-stock> — real stock of the selected size; refreshed when the
 * customer picks another size (Horizon sends the new section HTML).
 * ------------------------------------------------------------------ */

class RtStock extends HTMLElement {
  connectedCallback() {
    this.scope = this.closest('.shopify-section, dialog');
    this.onSelect = (event) => {
      const target = event.target;
      if (!(target instanceof Element) || target.closest('product-card')) return;
      const promise = /** @type {any} */ (event).promise;
      promise
        ?.then((/** @type {any} */ result) => {
          const detail = result?.detail;
          const doc = detail?.html;
          if (!doc) return;
          if (!detail.newProduct && detail.productId && String(detail.productId) !== this.dataset.productId) return;
          const next = doc.querySelector(`rt-stock[data-block-id="${this.dataset.blockId}"]`);
          if (!next) return;
          this.className = next.className;
          this.innerHTML = next.innerHTML;
          if (next.dataset.productId) this.dataset.productId = next.dataset.productId;
        })
        .catch(() => {});
    };
    this.scope?.addEventListener('shopify:product:select', this.onSelect);
  }

  disconnectedCallback() {
    this.scope?.removeEventListener('shopify:product:select', this.onSelect);
  }
}

/* "Show more" for long lists (reviews) */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest('[data-rt-more]');
  if (!(button instanceof HTMLElement)) return;
  const scope = button.closest('.shopify-section, section') || document;
  const step = Number(button.dataset.step) || 6;
  const hidden = [...scope.querySelectorAll('[data-rt-more-item][hidden]')];
  hidden.slice(0, step).forEach((item) => {
    item.hidden = false;
  });
  if (hidden.length <= step && button.parentElement) button.parentElement.hidden = true;
});

/* ------------------------------------------------------------------ *
 * Register
 * ------------------------------------------------------------------ */

const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

define('rt-money', RtMoney);
define('rt-stock', RtStock);
define('rt-count', RtCount);
define('rt-tabs', RtTabs);
define('rt-picker', RtPicker);
define('rt-pack-builder', RtPackBuilder);
define('rt-pack-offers', RtPackOffers);
define('rt-ship-bar', RtShipBar);

const boot = () => {
  scanReveals();
  renderPackCalcs();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target instanceof Element ? event.target : document;
  scanReveals(target);
  renderPackCalcs(target);
});

// Cart re-renders (morph) can bring in new reveal targets or money placeholders.
document.addEventListener('shopify:cart:lines-update', (event) => {
  const promise = /** @type {any} */ (event).promise;
  promise
    ?.then(() => {
      requestAnimationFrame(() => {
        document.querySelectorAll('rt-money').forEach((el) => /** @type {RtMoney} */ (el).render?.());
        document.querySelectorAll('rt-ship-bar').forEach((el) => /** @type {RtShipBar} */ (el).render?.());
        scanReveals();
      });
    })
    .catch(() => {});
});

export { money, RtPicker };